import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DroughtAnalyticsView, TargetSummaryRow } from '../../analytics/drought-view.ts';
import { formatDecimal, formatHours, formatInteger, formatPercent } from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { useDataset } from '../data/DatasetContext.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';

export function OverviewPage() {
  const navigate = useNavigate();
  const { summary } = useDataset();
  const [view, setView] = useState<DroughtAnalyticsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchJson<DroughtAnalyticsView>('/api/analytics/drought', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedTarget: 2 }),
    })
      .then((value) => {
        if (!cancelled) {
          setView(value);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Failed to load overview');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <StatusMessage kind="error">{error}</StatusMessage>;
  }
  if (!view) {
    return <StatusMessage kind="loading">Loading dataset overview</StatusMessage>;
  }

  const dataset = view.dataset;
  const knownGaps = dataset.completeness.knownTimelineGaps;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold">Overview</h1>
      </header>

      <section aria-labelledby="dataset-heading" className="rounded border border-line bg-panel p-4">
        <h2 id="dataset-heading" className="text-xs font-semibold uppercase tracking-wide text-muted">
          Dataset
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Metric label="Rounds" value={formatInteger(dataset.rounds)} />
          <Metric label="Collected Time" value={formatHours(dataset.collectedHours)} />
          <Metric label="Elapsed Time" value={formatHours(dataset.elapsedHours ?? dataset.periodHours)} />
          <Metric label="Collection Coverage" value={formatPercent(dataset.coverage, 1)} />
          <Metric label="Rounds/hour" value={formatDecimal(dataset.roundsPerHour, 2)} />
          <Metric label="Continuity Gaps" value={formatInteger(knownGaps)} />
          <Metric label="Integrity" value={dataset.integrity} />
        </dl>
        <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted">
          <div>
            Sequence: {formatInteger(summary?.minSequence)}–{formatInteger(summary?.maxSequence)}
          </div>
          <div>Confirmed: {formatInteger(summary?.confirmed)}</div>
          <div>Recovered: {formatInteger(summary?.recovered)}</div>
          <div>Uncertain: {formatInteger(summary?.uncertain)}</div>
          <div>Known gaps: {formatInteger(knownGaps)}</div>
        </dl>
      </section>

      <section aria-labelledby="targets-heading" className="rounded border border-line bg-panel">
        <div className="border-b border-line px-4 py-3">
          <h2 id="targets-heading" className="text-xs font-semibold uppercase tracking-wide text-muted">
            Target summary
          </h2>
        </div>
        <div className="table-scroll">
          <table className="min-w-[860px] w-full text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Target</th>
                <th className="px-3 py-2 font-medium">Hit rate</th>
                <th className="px-3 py-2 font-medium" title="Open miss run at the end of the sample.">
                  Current drought
                </th>
                <th
                  className="px-3 py-2 font-medium"
                  title="Percentage of completed historical droughts shorter than the current open drought."
                >
                  Current percentile
                </th>
                <th className="px-3 py-2 font-medium">Mean</th>
                <th className="px-3 py-2 font-medium">Median</th>
                <th className="px-3 py-2 font-medium">P75</th>
                <th className="px-3 py-2 font-medium">P90</th>
                <th className="px-3 py-2 font-medium">P95</th>
                <th className="px-3 py-2 font-medium">P99</th>
                <th className="px-3 py-2 font-medium" title="Longest completed drought. Open droughts are excluded.">
                  Longest
                </th>
                <th
                  className="px-3 py-2 font-medium"
                  title="Number of completed droughts used for distribution statistics."
                >
                  Samples
                </th>
              </tr>
            </thead>
            <tbody>
              {view.targetSummary.map((row) => (
                <TargetRow
                  key={row.targetMultiplier}
                  row={row}
                  onSelect={() => navigate(`/droughts?target=${row.targetMultiplier}`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Metric(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className="text-base font-medium">{props.value}</dd>
    </div>
  );
}

function TargetRow(props: { row: TargetSummaryRow; onSelect: () => void }) {
  const row = props.row;
  return (
    <tr
      className="cursor-pointer border-b border-line/70 hover:bg-soft"
      tabIndex={0}
      onClick={props.onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          props.onSelect();
        }
      }}
    >
      <td className="px-3 py-2 font-medium">{row.targetMultiplier}x</td>
      <td className="px-3 py-2">{formatPercent(row.hitRate, 2)}</td>
      <td className="px-3 py-2">{formatInteger(row.currentDrought)}</td>
      <td className="px-3 py-2">
        {row.currentDroughtPercentilePercent === null
          ? '—'
          : formatPercent(row.currentDroughtPercentilePercent / 100, 1)}
      </td>
      <td className="px-3 py-2">{formatDecimal(row.mean, 2)}</td>
      <td className="px-3 py-2">{formatDecimal(row.median, 2)}</td>
      <td className="px-3 py-2">{formatDecimal(row.p75, 2)}</td>
      <td className="px-3 py-2">{formatDecimal(row.p90, 2)}</td>
      <td className="px-3 py-2">{formatDecimal(row.p95, 2)}</td>
      <td className="px-3 py-2">{formatDecimal(row.p99, 2)}</td>
      <td className="px-3 py-2">{formatInteger(row.longestCompleted)}</td>
      <td className="px-3 py-2">{formatInteger(row.completedDroughtSamples)}</td>
    </tr>
  );
}
